import _noop from 'lodash/noop';
import { type JSX, useCallback, useContext } from 'react';
import type { EmitPageEventCta } from '../../../components/Notifications/types';
import { InterfaceAppStoreContext } from '../../stores/InterfaceStore/context';
import { useEmitPageEventCtaAction } from './hooks/useEmitPageEventCtaAction';

type Props = {
  cta: EmitPageEventCta;
  children: ({ onClick }: { onClick: () => void }) => JSX.Element;
};

function EmitPageEventCtaActionProvider({ cta, children }: Props) {
  const onClick = useEmitPageEventCtaAction();

  const handleClick = useCallback(() => {
    onClick({ cta });
  }, [cta, onClick]);

  return children({ onClick: handleClick });
}

function EmitPageEventCtaActionProviderContainer(props: Props) {
  const _useStore = useContext(InterfaceAppStoreContext);

  /**
   * CTA won't work if notification is sent to platform with cta of type emitPageEvent(unexpected scenario).
   * Hence, don't wrap EmitPageEventCtaActionProvider in such a case, so that notification is at least rendered.
   * Safe check in case when cta of type emitPageEvent was added by any other means than the notification node config in automations.
   * It is not possible to add cta of type emitPageEvent from the notification node config in automations unless applicationId is selected.
   * If applicationId is selected, then it means that notification will go to a LC app and hence EmitPageEventCtaActionProvider will be opened in a LC context.
   */
  if (!_useStore) {
    return props.children({ onClick: _noop });
  }

  return <EmitPageEventCtaActionProvider {...props} />;
}

export { EmitPageEventCtaActionProviderContainer as EmitPageEventCtaActionProvider };
